import type { ReactNode } from 'react';

import type { DataViewFilter } from 'components/DataView';
import { BundleState, ContainerType, ProductFlow, type BundlesFilterInput, type DateFilter } from 'data/__generated__';
import { internalUserSource, siteSource, warehouseColumnSource } from 'data/entity-sources/sources';

import { ContainerTypeTag } from '../components/ContainerTypeTag';
import { StatusTag } from '../components/StatusTag';
import { BundleTypeTag } from '../components/TypeTag';

import { CONTAINER_TYPE_LABELS, type BundleRow } from './bundlesColumns';
import { BUNDLE_STATES_LABELS } from './states';
import { BUNDLE_TYPES_LABELS } from './types';

/**
 * Une page large plutôt qu'une pagination fine : le kanban regroupe en colonnes, et une page courte
 * laisserait croire qu'une colonne est vide. Le périmètre par défaut (la préparation à venir) tient
 * très largement dedans — une journée de préparation compte quelques dizaines de bundles.
 */
export const BUNDLES_PAGE_SIZE = 500;

/**
 * Le périmètre par défaut : la préparation à venir. Aujourd'hui EN FAIT PARTIE — c'est le travail en
 * cours, et c'est le jour dont parle la barre de sélection par flux.
 */
function getUpcomingPreparationRange(): DateFilter {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return { gte: startOfToday.getTime() };
}

const UPCOMING_PREPARATION_RANGE = getUpcomingPreparationRange();

const STATE_OPTIONS = Object.values(BundleState).map((state) => ({
    value: state,
    label: BUNDLE_STATES_LABELS[state] ?? state,
}));

const FLOW_OPTIONS = Object.values(ProductFlow).map((flow) => ({
    value: flow,
    label: BUNDLE_TYPES_LABELS[flow] ?? flow,
}));

const CONTAINER_TYPE_OPTIONS = Object.values(ContainerType).map((containerType) => ({
    value: containerType,
    label: CONTAINER_TYPE_LABELS[containerType] ?? containerType,
}));

// `renderOption` ne reçoit que la valeur en `string` : les puces sont rendues d'avance, indexées par
// cette valeur, plutôt que re-typées à l'aveugle.
const STATE_TAGS: Record<string, ReactNode> = Object.fromEntries(
    Object.values(BundleState).map((state) => [state, <StatusTag key={state} state={state} />]),
);

const FLOW_TAGS: Record<string, ReactNode> = Object.fromEntries(
    Object.values(ProductFlow).map((flow) => [flow, <BundleTypeTag key={flow} type={flow} />]),
);

const CONTAINER_TYPE_TAGS: Record<string, ReactNode> = Object.fromEntries(
    Object.values(ContainerType).map((containerType) => [
        containerType,
        <ContainerTypeTag key={containerType} type={containerType} />,
    ]),
);

/**
 * Aucun `accessor` : tous ces filtres partent au SERVEUR, via `bundlesFilter`. La lecture est paginée,
 * filtrer les lignes déjà chargées ne regarderait que la page en cours en laissant croire qu'il n'y a
 * rien derrière.
 *
 * Le périmètre de travail est porté par la chip « Date de préparation » et par elle seule : ce que le
 * serveur reçoit est exactement ce que la chip affiche, et l'utilisateur peut l'élargir ou l'effacer.
 */
export const BUNDLE_FILTERS: DataViewFilter<BundleRow>[] = [
    {
        key: 'scheduledPreparationDate',
        title: 'Date de préparation',
        type: 'date-range',
        defaultValue: UPCOMING_PREPARATION_RANGE,
    },
    {
        key: 'state',
        title: 'Statut',
        type: 'multi-select',
        options: STATE_OPTIONS,
        renderOption: ({ value }) => STATE_TAGS[value] ?? null,
    },
    {
        key: 'type',
        title: 'Flux',
        type: 'multi-select',
        options: FLOW_OPTIONS,
        renderOption: ({ value }) => FLOW_TAGS[value] ?? null,
    },
    {
        key: 'containerType',
        title: 'Contenant',
        type: 'multi-select',
        options: CONTAINER_TYPE_OPTIONS,
        renderOption: ({ value }) => CONTAINER_TYPE_TAGS[value] ?? null,
    },
    {
        key: 'siteIds',
        title: 'Site',
        type: 'async-multi-select',
        source: siteSource,
    },
    {
        key: 'warehouseColumnIds',
        title: 'Entrepôt',
        type: 'async-multi-select',
        source: warehouseColumnSource,
    },
    {
        key: 'operatorIds',
        title: 'Opérateur.ice',
        type: 'async-multi-select',
        source: internalUserSource,
    },
    {
        key: 'createdAt',
        title: 'Créé le',
        type: 'date-range',
    },
];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function toIdList(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const ids = value.filter((item): item is string => typeof item === 'string');
    return ids.length ? ids : undefined;
}

/**
 * Les valeurs d'une chip à choix multiples, filtrées par la liste des valeurs admises : une valeur
 * inventée dans l'URL est ignorée plutôt que renvoyée telle quelle au serveur.
 */
function toEnumList<TValue extends string>(value: unknown, allowed: TValue[]): TValue[] | undefined {
    const selected = toIdList(value);
    if (!selected) {
        return undefined;
    }
    const matched = allowed.filter((candidate) => selected.includes(candidate));
    return matched.length ? matched : undefined;
}

function toDateFilter(value: unknown): DateFilter | undefined {
    if (!isRecord(value)) {
        return undefined;
    }
    const gte = typeof value.gte === 'number' ? value.gte : undefined;
    const lte = typeof value.lte === 'number' ? value.lte : undefined;
    return gte === undefined && lte === undefined ? undefined : { gte, lte };
}

/**
 * L'état de filtre de la `DataView` → les variables de la query. Une clé absente ou vide n'est pas
 * envoyée : côté serveur, `null` et « tableau vide » ne veulent pas dire la même chose que « non filtré ».
 */
export function toBundlesFilterInput(filterState: Record<string, unknown>): BundlesFilterInput {
    const searchValue = typeof filterState.searchValue === 'string' ? filterState.searchValue.trim() : '';

    return {
        // `searchValue` est la clé sous laquelle la `DataView` range la recherche.
        searchValue: searchValue || undefined,
        state: toEnumList(filterState.state, Object.values(BundleState)),
        type: toEnumList(filterState.type, Object.values(ProductFlow)),
        containerType: toEnumList(filterState.containerType, Object.values(ContainerType)),
        siteIds: toIdList(filterState.siteIds),
        warehouseColumnIds: toIdList(filterState.warehouseColumnIds),
        operatorIds: toIdList(filterState.operatorIds),
        scheduledPreparationDate: toDateFilter(filterState.scheduledPreparationDate),
        createdAt: toDateFilter(filterState.createdAt),
    };
}

/**
 * Le périmètre du premier rendu, produit par la MÊME fonction que les rendus suivants : la `DataView`
 * ne remonte son état qu'après le montage, un périmètre différent ici ferait une requête pour rien.
 */
export const DEFAULT_BUNDLE_FILTERS: BundlesFilterInput = toBundlesFilterInput({
    scheduledPreparationDate: UPCOMING_PREPARATION_RANGE,
});
